import {
    CheckCircleOutlined,
    CloudSyncOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    EditOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Empty,
    Popconfirm,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip
} from "antd";
import React, { useEffect, useState } from "react";
import { getSyncConfig, syncSingleModel } from "../../services/syncService";
import { deleteTurno, getAllTurnos } from "../../services/turnoService";

const TurnoList = ({ refreshTrigger, onEditTurno }) => {
    const [turnos, setTurnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncingAll, setSyncingAll] = useState(false);
    const isMobile = window.innerWidth < 768;
    const [config, setConfig] = useState({
        interval: 30,
        intervalUnit: "segundos",
    });

    // Cargar la configuración al inicio
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const syncConfig = await getSyncConfig();
                setConfig(syncConfig);
            } catch (error) {
                console.error("Error al cargar configuración:", error);
            }
        };

        fetchConfig();
    }, []);

    // Cargar turnos al inicio y cuando cambie el trigger
    useEffect(() => {
        loadTurnos();
    }, [refreshTrigger]);

    // Actualizar la lista según el intervalo configurado
    useEffect(() => {
        const interval = setInterval(() => {
            loadTurnos();
        }, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000)); // Convertir a milisegundos

        // Limpiar intervalo al desmontar
        return () => clearInterval(interval);
    }, [config]);

    const loadTurnos = async () => {
        try {
            console.log("Cargando turnos...");
            setLoading(true);
            const turnoList = await getAllTurnos();
            setTurnos(turnoList);
        } catch (error) {
            window.messageApi.error("Error al cargar turnos: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteTurno(id);
            // Actualizar la lista de turnos
            setTurnos(turnos.filter((turno) => turno.id !== id));
            window.messageApi.success("Turno eliminado correctamente");
        } catch (error) {
            window.messageApi.error("Error al eliminar turno: " + error.message);
        }
    };

    const handleEdit = (turno) => {
        if (onEditTurno) {
            onEditTurno(turno);
        }
    };

    // Renderizar el estado de sincronización
    const renderSyncStatus = (pendingSync, lastSynced) => {
        if (pendingSync === 1) {
            return (
                <Tooltip title="Pendiente de sincronizar">
                    <Tag icon={<CloudUploadOutlined />} color="warning">
                        Pendiente
                    </Tag>
                </Tooltip>
            );
        } else {
            const syncDate = lastSynced
                ? new Date(lastSynced).toLocaleString()
                : "Nunca";

            return (
                <Tooltip title={`Última sincronización: ${syncDate}`}>
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Sincronizado
                    </Tag>
                </Tooltip>
            );
        }
    };

    // Sincronizar todos los turnos
    const handleSyncAll = async () => {
        try {
            setSyncingAll(true);
            await syncSingleModel("turnos");
            // Recargar la lista para reflejar los cambios
            await loadTurnos();
            window.messageApi.success("Sincronización de turnos completada");
        } catch (error) {
            window.messageApi.error("Error durante la sincronización: " + error.message);
        } finally {
            setSyncingAll(false);
        }
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
        },
        {
            title: "No",
            dataIndex: "No",
            key: "No",
            render: (text) => text || "-",
        },
        {
            title: "Descripción",
            dataIndex: "DescripcionTurno",
            key: "DescripcionTurno",
        },
        {
            title: "Inicio",
            dataIndex: "HorarioInicio",
            key: "HorarioInicio",
        },
        {
            title: "Fin",
            dataIndex: "HorarioFin",
            key: "HorarioFin",
            render: (text) => text || "-",
        },
        {
            title: "Estado",
            key: "syncStatus",
            render: (_, record) =>
                renderSyncStatus(record.pendingSync, record.lastSynced),
        },
        {
            title: "Acciones",
            key: "actions",
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record)}
                    >
                        Editar
                    </Button>
                    <Popconfirm
                        title="¿Está seguro de que desea eliminar este turno?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} size="small">
                            Eliminar
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return <Spin size="large" />;
    }

    if (turnos.length === 0) {
        return <Empty description="No hay turnos registrados aún" />;
    }

    return (
        <div className="turno-list-container">
            <Card
                title={isMobile ? "Turnos" : "Turnos Registrados"}
                extra={
                    <Button
                        icon={<CloudSyncOutlined />}
                        onClick={handleSyncAll}
                        loading={syncingAll}
                        type="primary"
                    >
                        {isMobile ? "" : "Sincronizar Todos"}
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={turnos.map((turno) => ({ ...turno, key: turno.id }))}
                    pagination={{ pageSize: 5 }}
                    bordered
                    size="middle"
                    scroll={{ x: "max-content" }}
                />
            </Card>
        </div>
    );
};

export default TurnoList; 